import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Icon } from 'components/Icon';
import { Notifications } from 'components/Notifications';
import { PageHeaderDropdownMenu } from 'components/Page/Header/PageHeaderDropdownMenu';
import {
  fetchNotifications,
  markNotificationsAsViewed,
} from 'modules/main/store/ducks/userNotifications';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { colors } from 'theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { isBrowser } from 'utils/browser';
import { getMappedNotificationIds } from 'utils/userNotifications';

const Tag = styled(Box)`
  position: absolute;
  right: 3px;
  top: 6px;
  padding: 4px;
  border-radius: 50%;
  background-color: ${colors.decorativePoppy};
`;

export function PageHeaderNotificationsDropdown() {
  const { user } = useUserEnvironment();

  const dispatch = useMainDispatch();

  const { notifications, isLoaded } = useMainSelector(
    (state) => state.userNotifications,
  );

  const unseenNotifications = useMemo(
    () => notifications.filter((notification) => !notification.viewed),
    [notifications],
  );

  const showBadge = useMemo(
    () => isBrowser() && unseenNotifications.length > 0,
    [unseenNotifications],
  );

  const handleView = useCallback(
    (viewedNotifications: MappedNotification[]) =>
      dispatch(
        markNotificationsAsViewed(
          viewedNotifications.flatMap((notification) =>
            getMappedNotificationIds(notification),
          ),
        ),
      ),
    [dispatch],
  );

  useEffect(() => {
    if (user?.hasNotifications && !isLoaded) {
      dispatch(fetchNotifications());
    }
  }, [user?.hasNotifications, dispatch, isLoaded]);

  return (
    <PageHeaderDropdownMenu
      right
      aria-label={getText('Notifications')}
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/no-unstable-nested-components
      title={() => (
        <Box position="relative">
          {showBadge ? <Tag /> : null}
          <Icon name="bell" size={22} />
        </Box>
      )}
      height={370}
      width={260}
      data-qa-id="notifications-dropdown"
      onClick={(open) => {
        if (open && notifications.length) {
          handleView(notifications);

          trackEvent('Displayed User Menu Notifications', {
            notification_types: notifications.reduce(
              (acc, notification) =>
                // @ts-expect-error TS(2769): No overload matches this call.
                acc.includes(notification.type)
                  ? acc
                  : [...acc, notification.type],
              [],
            ),
          });
        }
      }}
    >
      {({ closeMenu }) => (
        <Notifications notifications={notifications} onClick={closeMenu} />
      )}
    </PageHeaderDropdownMenu>
  );
}
